import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { clsx } from '../../../ds/clsx';
import { useStableId } from '../../../ds/hooks';
import styles from './Booking.module.css';

/**
 * The fields of the booking form (Buchungsseite 02/10): 12.5 px label with the accent „ *", 14 px value in
 * the inset well (16 px below 768 px), the danger border and the 12 px message; hint in `--text-subtle`.
 */

interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  hint?: string | undefined;
  error?: string | null | undefined;
}

export function BookingField({ label, hint, error, required, className, ...rest }: FieldProps) {
  const id = useStableId();
  const messageId = `${id}-message`;
  return (
    <div className={clsx(styles.field, className)}>
      <label htmlFor={id} className={styles.fieldLabel}>
        {label}
        {required ? (
          <span className={styles.required} aria-hidden="true">
            {' '}
            *
          </span>
        ) : null}
      </label>
      <input
        id={id}
        className={clsx(styles.input, error && styles.inputInvalid)}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={error || hint ? messageId : undefined}
        {...rest}
      />
      {error ? (
        <span id={messageId} className={styles.fieldError}>
          {error}
        </span>
      ) : hint ? (
        <span id={messageId} className={styles.fieldHint}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: string;
  hint?: string | undefined;
  error?: string | null | undefined;
  options: readonly { value: string; label: string }[];
}

/** A select in the look of `BookingField` (D-454: „Anrede" Herr/Frau). */
export function BookingSelect({ label, hint, error, required, className, options, ...rest }: SelectProps) {
  const id = useStableId();
  const messageId = `${id}-message`;
  return (
    <div className={clsx(styles.field, className)}>
      <label htmlFor={id} className={styles.fieldLabel}>
        {label}
        {required ? (
          <span className={styles.required} aria-hidden="true">
            {' '}
            *
          </span>
        ) : null}
      </label>
      <select
        id={id}
        className={clsx(styles.input, error && styles.inputInvalid)}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={error || hint ? messageId : undefined}
        {...rest}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error ? (
        <span id={messageId} className={styles.fieldError}>
          {error}
        </span>
      ) : hint ? (
        <span id={messageId} className={styles.fieldHint}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

interface AreaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  label: string;
  hint?: string | undefined;
  error?: string | null | undefined;
}

export function BookingTextarea({ label, hint, error, required, className, ...rest }: AreaProps) {
  const id = useStableId();
  const messageId = `${id}-message`;
  return (
    <div className={clsx(styles.field, className)}>
      <label htmlFor={id} className={styles.fieldLabel}>
        {label}
        {required ? (
          <span className={styles.required} aria-hidden="true">
            {' '}
            *
          </span>
        ) : null}
      </label>
      <textarea
        id={id}
        className={clsx(styles.input, styles.textarea, error && styles.inputInvalid)}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={error || hint ? messageId : undefined}
        {...rest}
      />
      {error ? (
        <span id={messageId} className={styles.fieldError}>
          {error}
        </span>
      ) : hint ? (
        <span id={messageId} className={styles.fieldHint}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

interface ConsentProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  error?: string | null | undefined;
  /** The „Datenschutz" link (the organization's privacy text), omitted when none is published. */
  privacy?: ReactNode | undefined;
}

/** „Ich bin einverstanden, dass meine Angaben zur Terminvorbereitung gespeichert werden. Datenschutz *" */
export function ConsentCheckbox({ checked, onChange, error, privacy }: ConsentProps) {
  const id = useStableId();
  const errorId = `${id}-error`;
  return (
    <div className={styles.consentWrap}>
      <div className={styles.consent}>
        <input
          id={id}
          type="checkbox"
          className={styles.consentInput}
          checked={checked}
          required
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span className={clsx(styles.consentBox, error && styles.consentBoxInvalid)} aria-hidden="true" />
        <span className={styles.consentText}>
          <label htmlFor={id}>
            Ich bin einverstanden, dass meine Angaben zur Terminvorbereitung gespeichert werden.
          </label>{' '}
          {privacy}
          <span className={styles.required} aria-hidden="true">
            {' '}
            *
          </span>
        </span>
      </div>
      {error ? (
        <span id={errorId} className={styles.fieldError}>
          {error}
        </span>
      ) : null}
    </div>
  );
}
